/**
 * Partner Matchday Plannings Overview Page
 * Shows all matchday plannings assigned to the partner
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePartnerAuth } from '../contexts/PartnerAuthContext';
import { List, LayoutGrid, CalendarDays, ChevronUp, ChevronDown, ArrowLeft, ArrowRight } from 'lucide-react';
import { matchdayPlanningApi } from '../services/matchdayPlanningApi';
import { MatchdayPlanning } from '../types/matchdayPlanning';

type ViewMode = 'cards' | 'plantafel' | 'calendar';

const PartnerMatchdayPlannings: React.FC = () => {
  const navigate = useNavigate();
  const { user } = usePartnerAuth();
  const [plannings, setPlannings] = useState<MatchdayPlanning[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    loadPlannings();
  }, []);

  const loadPlannings = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Loading plannings for partner:', user?.partnerId);
      const data = await matchdayPlanningApi.getAllPlannings();
      console.log('Received plannings:', data);
      
      setPlannings(data);
    } catch (err: any) {
      console.error('Error loading plannings:', err);
      setError(err.message || 'Failed to load plannings');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPlanning = (planningId: number) => {
    navigate(`/partner/matchday-planning/${planningId}`);
  };

  if (loading) {
    return (
      <div className="page-container" style={containerStyle}>
        <div style={loadingStyle}>Loading your assigned matches...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container" style={containerStyle}>
        <div style={errorBoxStyle}>
          <span style={errorIconStyle}>!</span>
          {error}
        </div>
        <button onClick={loadPlannings} style={retryButtonStyle}>
          Retry
        </button>
      </div>
    );
  }

  const sortedPlannings = [...plannings].sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  return (
    <div className="page-container" style={containerStyle}>
      <div style={headerContainerStyle}>
        <div>
          <h1 style={titleStyle}>Upcoming Matches</h1>
          <p style={subtitleStyle}>
            {plannings.length} Spiele — {user?.displayName || user?.email}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')} style={sortBtnStyle}>
            {sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          <div style={viewToggleContainerStyle}>
            <button onClick={() => setViewMode('plantafel')} style={viewMode === 'plantafel' ? viewToggleActiveStyle : viewToggleButtonStyle} title="Liste (Plantafel)"><List size={14} /></button>
            <button onClick={() => setViewMode('cards')} style={viewMode === 'cards' ? viewToggleActiveStyle : viewToggleButtonStyle} title="Kacheln"><LayoutGrid size={14} /></button>
            <button onClick={() => setViewMode('calendar')} style={viewMode === 'calendar' ? viewToggleActiveStyle : viewToggleButtonStyle} title="Kalender"><CalendarDays size={14} /></button>
          </div>
          <button onClick={() => navigate('/partner/dashboard')} style={backButtonStyle}>
            <ArrowLeft size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Back
          </button>
        </div>
      </div>

      {plannings.length === 0 ? (
        <div style={emptyStateStyle}>
          <div style={emptyIconStyle}>—</div>
          <h2 style={emptyTitleStyle}>No Matches Assigned</h2>
          <p style={emptyTextStyle}>
            You don't have any matches assigned to you yet. Please contact your administrator.
          </p>
        </div>
      ) : (
        <>
          {/* Cards View */}
          {viewMode === 'cards' && (
            <div style={planningsGridStyle}>
              {sortedPlannings.map((planning) => (
                <div key={planning.id} style={planningCardStyle}>
                  <div style={cardHeaderStyle}>
                    <h3 style={cardTitleStyle}>{planning.name}</h3>
                    <span style={statusBadgeStyle(planning.status)}>{planning.status}</span>
                  </div>
                  <div style={cardBodyStyle}>
                    <div style={infoRowStyle}>
                      <span style={labelStyle}>Date:</span>
                      <span style={valueStyle}>
                        {new Date(planning.date).toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </span>
                    </div>
                    <div style={infoRowStyle}>
                      <span style={labelStyle}>Kickoff:</span>
                      <span style={valueStyle}>{planning.kickoffTime}</span>
                    </div>
                    <div style={infoRowStyle}>
                      <span style={labelStyle}>Segments:</span>
                      <span style={valueStyle}>{planning.segments?.length || 0}</span>
                    </div>
                  </div>
                  <div style={cardFooterStyle}>
                    <button onClick={() => handleViewPlanning(planning.id)} style={viewButtonStyle}>View Details <ArrowRight size={12} style={{ display: 'inline', verticalAlign: 'middle', marginLeft: 4 }} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Plantafel (Table) View */}
          {viewMode === 'plantafel' && (
            <div className="table-responsive" style={tableContainerStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Datum</th>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Anstoß</th>
                    <th style={thStyle}>Segmente</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPlannings.map((planning) => (
                    <tr key={planning.id} style={trStyle}>
                      <td style={tdStyle}>
                        {new Date(planning.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </td>
                      <td style={tdStyle}>{planning.name}</td>
                      <td style={tdStyle}>{planning.kickoffTime || '-'}</td>
                      <td style={tdStyle}>{planning.segments?.length || 0}</td>
                      <td style={tdStyle}>
                        <span style={statusBadgeStyle(planning.status)}>{planning.status}</span>
                      </td>
                      <td style={tdStyle}>
                        <button onClick={() => handleViewPlanning(planning.id)} style={tableActionBtnStyle}>Details</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Calendar View */}
          {viewMode === 'calendar' && (
            <div style={calendarContainerStyle}>
              {(() => {
                const monthGroups: Record<string, MatchdayPlanning[]> = {};
                for (const p of sortedPlannings) {
                  const d = new Date(p.date);
                  const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                  if (!monthGroups[key]) monthGroups[key] = [];
                  monthGroups[key].push(p);
                }
                return Object.entries(monthGroups).map(([monthKey, monthPlannings]) => {
                  const [year, month] = monthKey.split('-').map(Number);
                  const firstDay = new Date(year, month - 1, 1);
                  const lastDay = new Date(year, month, 0);
                  const startWeekday = (firstDay.getDay() + 6) % 7;
                  const daysInMonth = lastDay.getDate();
                  const monthName = firstDay.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

                  const dayMap: Record<number, MatchdayPlanning[]> = {};
                  for (const p of monthPlannings) {
                    const day = new Date(p.date).getDate();
                    if (!dayMap[day]) dayMap[day] = [];
                    dayMap[day].push(p);
                  }

                  const cells: React.ReactNode[] = [];
                  for (let i = 0; i < startWeekday; i++) {
                    cells.push(<div key={`empty-${i}`} style={calCellEmptyStyle} />);
                  }
                  for (let day = 1; day <= daysInMonth; day++) {
                    const dayPlannings = dayMap[day] || [];
                    const isToday = new Date().getDate() === day && new Date().getMonth() === month - 1 && new Date().getFullYear() === year;
                    cells.push(
                      <div key={day} style={{ ...calCellStyle, ...(isToday ? calTodayStyle : {}) }}>
                        <div style={calDayNumStyle}>{day}</div>
                        {dayPlannings.map((p, idx) => (
                          <div key={idx} style={calMatchStyleDisabled} title={p.name}>
                            <span style={{ fontSize: 11, lineHeight: '1.2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                              {p.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  }

                  return (
                    <div key={monthKey} style={{ marginBottom: 24 }}>
                      <h3 style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 600, color: '#2c3e50' }}>{monthName}</h3>
                      <div style={calHeaderRowStyle}>
                        {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(d => (
                          <div key={d} style={calHeaderCellStyle}>{d}</div>
                        ))}
                      </div>
                      <div style={calGridStyle}>{cells}</div>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Styles
const containerStyle: React.CSSProperties = {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '2rem',
};

const headerContainerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '2rem',
};

const titleStyle: React.CSSProperties = {
  fontSize: '2rem',
  fontWeight: 'bold',
  color: '#2c3e50',
  marginBottom: '0.5rem',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: '1rem',
  color: '#7f8c8d',
};

const backButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  fontSize: '1rem',
  fontWeight: '600',
  color: '#3498db',
  backgroundColor: 'white',
  border: '2px solid #3498db',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const loadingStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '3rem',
  fontSize: '1.2rem',
  color: '#7f8c8d',
};

const errorBoxStyle: React.CSSProperties = {
  backgroundColor: '#fee',
  border: '1px solid #fcc',
  borderRadius: '6px',
  padding: '1rem',
  marginBottom: '1.5rem',
  color: '#c33',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
};

const errorIconStyle: React.CSSProperties = {
  fontSize: '1.2rem',
};

const retryButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  fontSize: '1rem',
  fontWeight: '600',
  color: 'white',
  backgroundColor: '#3498db',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

const emptyStateStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '4rem 2rem',
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
};

const emptyIconStyle: React.CSSProperties = {
  fontSize: '4rem',
  marginBottom: '1rem',
};

const emptyTitleStyle: React.CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '#2c3e50',
  marginBottom: '0.5rem',
};

const emptyTextStyle: React.CSSProperties = {
  fontSize: '1rem',
  color: '#7f8c8d',
};

const planningsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
  gap: '1.5rem',
};

const planningCardStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  transition: 'transform 0.2s, box-shadow 0.2s',
  cursor: 'pointer',
};

const cardHeaderStyle: React.CSSProperties = {
  padding: '1.5rem',
  borderBottom: '1px solid #e0e0e0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const cardTitleStyle: React.CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 'bold',
  color: '#2c3e50',
  margin: 0,
};

const statusBadgeStyle = (status: string): React.CSSProperties => {
  const baseStyle: React.CSSProperties = {
    padding: '0.25rem 0.75rem',
    borderRadius: '12px',
    fontSize: '0.85rem',
    fontWeight: '600',
  };

  switch (status.toLowerCase()) {
    case 'draft':
      return { ...baseStyle, backgroundColor: '#fff3cd', color: '#856404' };
    case 'active':
      return { ...baseStyle, backgroundColor: '#d4edda', color: '#155724' };
    case 'completed':
      return { ...baseStyle, backgroundColor: '#d1ecf1', color: '#0c5460' };
    default:
      return { ...baseStyle, backgroundColor: '#e2e3e5', color: '#383d41' };
  }
};

const cardBodyStyle: React.CSSProperties = {
  padding: '1.5rem',
};

const infoRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '0.75rem',
};

const labelStyle: React.CSSProperties = {
  fontSize: '0.9rem',
  color: '#7f8c8d',
  fontWeight: '600',
};

const valueStyle: React.CSSProperties = {
  fontSize: '0.9rem',
  color: '#2c3e50',
};

const cardFooterStyle: React.CSSProperties = {
  padding: '1rem 1.5rem',
  backgroundColor: '#f8f9fa',
  borderTop: '1px solid #e0e0e0',
};

const viewButtonStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.75rem',
  fontSize: '1rem',
  fontWeight: '600',
  color: 'white',
  backgroundColor: '#3498db',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
};

const sortBtnStyle: React.CSSProperties = {
  padding: '8px 12px',
  backgroundColor: '#f8f9fa',
  border: '1px solid #dee2e6',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '14px',
};

const viewToggleContainerStyle: React.CSSProperties = {
  display: 'flex',
  borderRadius: '8px',
  overflow: 'hidden',
  border: '1px solid #dee2e6',
};

const viewToggleButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: '#f8f9fa',
  color: '#495057',
  border: 'none',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '500',
  transition: 'all 0.2s',
};

const viewToggleActiveStyle: React.CSSProperties = {
  ...viewToggleButtonStyle,
  backgroundColor: '#007bff',
  color: 'white',
};

const tableContainerStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #dee2e6',
  fontSize: '13px',
  fontWeight: 600,
  color: '#495057',
};

const trStyle: React.CSSProperties = {
  borderBottom: '1px solid #e9ecef',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontSize: '13px',
  color: '#2c3e50',
};

const tableActionBtnStyle: React.CSSProperties = {
  padding: '4px 12px',
  fontSize: '12px',
  fontWeight: 600,
  color: 'white',
  backgroundColor: '#3498db',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
};

const tableActionBtnDisabledStyle: React.CSSProperties = {
  ...tableActionBtnStyle,
  backgroundColor: '#b0bec5',
  cursor: 'default',
  opacity: 0.6,
};

const calendarContainerStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '20px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
};

const calGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '1px',
  backgroundColor: '#dee2e6',
};

const calHeaderRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '1px',
  backgroundColor: '#dee2e6',
  marginBottom: '1px',
};

const calHeaderCellStyle: React.CSSProperties = {
  backgroundColor: '#f8f9fa',
  padding: '6px 4px',
  textAlign: 'center',
  fontSize: 12,
  fontWeight: 600,
  color: '#6c757d',
};

const calCellStyle: React.CSSProperties = {
  backgroundColor: 'white',
  minHeight: 70,
  padding: 4,
  verticalAlign: 'top',
};

const calCellEmptyStyle: React.CSSProperties = {
  backgroundColor: '#fafafa',
  minHeight: 70,
};

const calTodayStyle: React.CSSProperties = {
  backgroundColor: '#e8f4fd',
};

const calDayNumStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: '#495057',
  marginBottom: 2,
};

const calMatchStyle: React.CSSProperties = {
  padding: '2px 4px',
  borderRadius: 4,
  marginBottom: 2,
  cursor: 'pointer',
  backgroundColor: '#e3f2fd',
};

const calMatchStyleDisabled: React.CSSProperties = {
  ...calMatchStyle,
  cursor: 'default',
  opacity: 0.7,
};

export default PartnerMatchdayPlannings;

